import { Subscription } from 'rxjs';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UserCredential } from '@angular/fire/auth';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { SsoProviderType } from 'src/lib/fliko/models/company/sso.data';
import { CheckDomainRes, ProvideWLAccessCtx } from 'src/lib/fliko/models/http/ssoRequest.data';
import { ErrorHandlingService } from 'src/app/shared/error-handling/error-handling.service';
import { AsyncHlp } from 'src/app/shared/helpers/async.helper';
import { AuthService } from 'src/app/shared/services/auth.service';
import { SessionStorageService } from 'src/app/shared/services/session-storage.service';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { CompanyApiService } from 'src/app/view-company/service/company-api.service';
import { ResetPasswordDialogComponent } from '../../components/reset-password-dialog/reset-password-dialog.component';
import { BreakData } from '../../services/models/break.data';
import { ContextDataService } from '../../services/context.data-service';
import { LoginCaptchaService } from '../../services/login-captcha.service';
import { RWDService } from '../../services/rwd.service';
import { UserService } from 'src/app/view/users/services/user.service';
import { ObjHlp } from 'src/lib/fliko/helpers/object.helper';
import { environment } from 'src/environments/environment';
import { UserStartRouteService } from '../../services/user-start-route.service';

/**
 * Login flow steps
 */
enum LoginStep {
  /** Enter email */
  EMAIL = 'email',
  /** Check domain and user for SSO login */
  CHECKING = 'checking',
  /** Enter password (traditional login) */
  PASSWORD = 'password',
  /** Login by SSO */
  SSO = 'sso',
  /** User is logged by SSO but don't have access to CRM */
  SSO_PENDING = 'sso_pending',
  /** User sent request access */
  SSO_REQUEST_SENT = 'sso_request_sent'
}

/**
 * Firebase Auth error interface
 */
interface AuthError {
  code: string;
  message?: string;
  customData?: any;
}

@Component({
  selector: 'app-sign-in',
  templateUrl: './sign-in.component.html',
  styleUrls: ['./sign-in.component.scss']
})
export class SignInComponent implements OnInit, OnDestroy {
  public readonly recaptchaSiteKey = environment.recaptchaSiteKey;
  public form: FormGroup;
  public showBreakText = false;
  public breakData: BreakData | null = null;
  public welcomeMsgKey: string = '';

  // SSO Flow State
  public currentStep: LoginStep = LoginStep.EMAIL;
  public isCheckingDomain = false;
  public isSubmittingSso = false;
  public pendingSsoEmail: string = '';
  public ssoConfig: CheckDomainRes | null = null;
  public isCaptchaRequired = false;

  private readonly recaptchaActionLogin = 'login_password';
  private returnUrl: string | null = null;
  private subscription$ = new Subscription();

  LoginStep = LoginStep; // Expose enum to template

  constructor(
    public rwdService: RWDService,
    private fb: FormBuilder,
    private auth: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private ls: LocalizationService,
    private errorHandler: ErrorHandlingService,
    private companyAS: CompanyApiService,
    private cdr: ChangeDetectorRef,
    private ctxDS: ContextDataService,
    private loginCaptchaSrv: LoginCaptchaService,
    private userService: UserService,
    private sessionStorageSrv: SessionStorageService,
    private userStartRouteService: UserStartRouteService
  ) {
    this.form = this.fb.group({
      email: this.fb.control('', [Validators.required, Validators.email]),
      password: this.fb.control('')
    });
    this.route.queryParams.subscribe((params) => (this.returnUrl = params.returnUrl || null));
    // TODO: add randomInt() to shared
    this.welcomeMsgKey = `login.welcome${(Math.random() * 9 + 1).toFixed(0)}`;
  }

  ngOnInit(): void {
    this.subscription$.add(this.ctxDS.breakData$.subscribe((breakData) => (this.breakData = breakData)));

    // Note: No longer checking redirect result - we use popup flow now
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }

  /**
   * Step 1: Check email for SSO configuration (domain or whitelist)
   * Called when user submits email in first step
   */
  public async checkEmailDomain(): Promise<void> {
    const emailCtrlVal = this.form.get('email');
    if (!emailCtrlVal || emailCtrlVal.invalid || this.isCheckingDomain) return;

    const email = emailCtrlVal.value;
    this.isCheckingDomain = true;
    this.currentStep = LoginStep.CHECKING;

    try {
      this.ssoConfig = await this.auth.checkSsoDomain(email);

      if (!ObjHlp.isEmpty(this.ssoConfig)) {
        this.clearCaptchaState();
        this.currentStep = LoginStep.SSO;

        // ! Force change detection
        this.cdr.detectChanges();
      } else {
        this.currentStep = LoginStep.PASSWORD;
        this.syncCaptchaState(email);
      }
    } catch (error) {
      this.errorHandler.showGlobalError(this.ls.translate('error.ssoCheck'), error);
      // ? On error, fallback to password login
      this.currentStep = LoginStep.PASSWORD;
      this.syncCaptchaState(email);
    } finally {
      this.isCheckingDomain = false;
    }
  }

  /**
   * Step 2a: Traditional password login
   * Called when user submits password
   */
  public async submitPasswordLogin(): Promise<void> {
    const emailCtrl = this.form.get('email');
    if (!emailCtrl || emailCtrl.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (!(await this.validateCaptcha())) {
      return;
    }

    const email = this.form.get('email')?.value;
    if (!email) return;

    this.auth.logIn(
      email,
      this.form.value.password,
      (result) => {
        this.loginCaptchaSrv.clearFailedAttempts(email);
        this.clearCaptchaState();
        this.handleLoginSuccess(result);
      },
      (error) => {
        if (this.loginCaptchaSrv.shouldCountAsFailedAttempt((error as AuthError)?.code)) {
          this.handleFailedPasswordAttempt(email);
        }
        this.handleLoginError(error);
      }
    );
  }

  /**
   * Step 2b: Login with Microsoft SSO
   * Uses popup flow to authenticate user via Microsoft OIDC provider
   * Opens Microsoft login in new window and handles the authentication result
   */
  public async loginWithMicrosoft(): Promise<void> {
    if (!this.ssoConfig || this.ssoConfig.provider !== SsoProviderType.MICROSOFT || this.isSubmittingSso) return;

    const email = this.form.get('email')?.value;
    if (!email) return;

    this.isSubmittingSso = true;

    try {
      // Use popup flow - opens in new window, keeps user on current page
      const result = await this.auth.signInWithMicrosoftPopup(email, this.ssoConfig.providerId, this.ssoConfig.tenantId);

      // Handle successful login - pass the domain email from form
      await this.handleLoginSuccess(result, email);
    } catch (error) {
      this.handleLoginError(error as AuthError);
    } finally {
      this.isSubmittingSso = false;
    }
  }

  /**
   * Handle successful login (both traditional and SSO)
   * @param {UserCredential} result     - Firebase auth result
   * @param {string}         [ssoEmail] - The domain email from login form (for SSO users only)
   */
  private async handleLoginSuccess(result: UserCredential, ssoEmail?: string): Promise<void> {
    if (!result.user.uid) return;

    const userId = result.user.uid;
    const isSSO = !!ssoEmail || this.currentStep === LoginStep.SSO;
    const email = ssoEmail || this.form.get('email')?.value || result.user.email;
    if (email) this.loginCaptchaSrv.clearFailedAttempts(email);
    this.clearCaptchaState();

    // ? For SSO - check if user has UserData (is provisioned)
    if (isSSO) {
      try {
        // HACK: Wait for user update to have certainty that user exists in database
        await AsyncHlp.wait(1000);
        const userData = await AsyncHlp.onFirst(this.userService.getUser$(userId));

        if (!userData) {
          // ? Use SSO email from form (passed as parameter) or fallback to form/result
          const userEmail = ssoEmail || this.form.get('email')?.value || result.user.email || '';
          const companyId = this.ssoConfig?.companyId;

          if (companyId) {
            try {
              // Get displayName and providerId from Firebase user
              const displayName = result.user.displayName || '';
              const providerData = result.user.providerData.find((pd) => pd.providerId.startsWith('oidc.'));
              const tenantId = providerData?.uid || userId;
              // TODO: detect from provider
              const providerType = SsoProviderType.MICROSOFT;

              // Check whitelist and auto-provision via backend
              const wlCtx: ProvideWLAccessCtx = {
                companyId,
                userId,
                email: userEmail,
                displayName,
                providerId: providerData?.providerId || '',
                tenantId: tenantId,
                providerType
              };
              const isProvisioned = await this.companyAS.checkWhitelistAccess(wlCtx);

              if (isProvisioned) {
                // HACK: Wait for user update to have certainty that user exists in database
                await AsyncHlp.wait(1000);
                // ? User was on whitelist and provisioned successfully - continue to normal logic
                this.auth.updateAfterSso(userId);
              } else {
                // ? Not on whitelist - show pending screen
                this.pendingSsoEmail = userEmail;
                this.currentStep = LoginStep.SSO_PENDING;
                this.cdr.detectChanges();
                return;
              }
            } catch (error) {
              this.errorHandler.showGlobalError(this.ls.translate('error.ssoRequestProcessing'), error);
              return;
            }
          } else {
            // ? No companyId - show pending screen
            this.pendingSsoEmail = userEmail;
            this.currentStep = LoginStep.SSO_PENDING;
            this.cdr.detectChanges();
            return;
          }
        }
      } catch {
        // ? In case of error - show pending message (keep user logged in);
        // Use SSO email from form to ensure we show the correct domain email
        this.pendingSsoEmail = ssoEmail || this.form.get('email')?.value || result.user.email || '';
        this.currentStep = LoginStep.SSO_PENDING;
        this.cdr.detectChanges();
        return;
      }
    }
    this.sessionStorageSrv.selectedUser.set(userId);

    if (this.breakData?.active) {
      this.showBreakText = true;
    } else {
      const returnUrl = await this.userStartRouteService.resolveLoginUrl(userId, this.returnUrl);
      this.returnUrl = returnUrl;
      this.router.navigateByUrl(returnUrl);
    }
  }

  /**
   * Handle login error (both traditional and SSO)
   * @param {AuthError} error - Firebase auth error
   */
  private handleLoginError(error: AuthError): void {
    let message: string = '';
    let sendToSentry = true;

    switch (error.code) {
      case 'auth/wrong-password':
      case 'auth/user-not-found':
      case 'auth/invalid-login-credentials':
      case 'auth/invalid-credential':
      case 'auth/missing-password':
        message = this.ls.translate('error.badPasswd');
        sendToSentry = false;
        break;
      case 'auth/too-many-requests':
        message = this.ls.translate('error.loginTooMany');
        sendToSentry = false;
        break;
      case 'auth/network-request-failed':
        message = this.ls.translate('error.network');
        sendToSentry = false;
        break;
      case 'auth/popup-closed-by-user':
      case 'auth/popup-blocked':
        message = this.ls.translate('error.ssoRequestProcessing');
        sendToSentry = false;
        break;
      case 'auth/cancelled-popup-request':
        // User closed popup - don't show error
        return;
      default:
        message = error.message || this.ls.translate('error.login');
    }

    this.errorHandler.showGlobalError(message, sendToSentry ? error : null);
  }

  /**
   * Get SSO provider display name for UI
   * Currently only Microsoft (OIDC) is supported
   */
  public getSsoProviderName(): string {
    return this.ssoConfig?.provider == SsoProviderType.MICROSOFT ? this.ls.translate('sso.microsoft') : '';
  }

  public resetPassword(): void {
    this.dialog.open(ResetPasswordDialogComponent, { data: this.form.value.email });
  }

  /**
   * Go back to email step (from pending or other steps)
   */
  public async backToEmail(): Promise<void> {
    // If user is in SSO_PENDING or SSO_REQUEST_SENT state, log them out
    if (this.currentStep === LoginStep.SSO_PENDING || this.currentStep === LoginStep.SSO_REQUEST_SENT) {
      await this.auth.logOut();
    }

    this.currentStep = LoginStep.EMAIL;
    this.pendingSsoEmail = '';
    this.ssoConfig = null;
    this.form.get('password')?.reset();
    this.clearCaptchaState();
  }

  public onRequestSent(): void {
    // Show confirmation message
    this.currentStep = LoginStep.SSO_REQUEST_SENT;
    this.cdr.detectChanges();
  }

  /**
   * Validates reCAPTCHA only when captcha is required for password login
   * @return {Promise<boolean>}
   */
  private async validateCaptcha(): Promise<boolean> {
    if (!this.isCaptchaRequired) return true;

    try {
      const isVerified = await this.loginCaptchaSrv.validateRecaptchaForLogin(this.recaptchaSiteKey, this.recaptchaActionLogin);
      if (isVerified) return true;
      this.errorHandler.showGlobalError('error.captchaInvalid');
      return false;
    } catch {
      this.errorHandler.showGlobalError('error.captchaInvalid');
      return false;
    }
  }

  /**
   * Sync captcha state with failed attempts tracked for provided email
   * @param {string | null | undefined} email
   */
  private syncCaptchaState(email: string | null | undefined): void {
    this.isCaptchaRequired = this.loginCaptchaSrv.requiresCaptcha(email);
    if (!this.isCaptchaRequired) this.clearCaptchaState();
  }

  /**
   * Handles failed password login attempt and enables captcha on threshold
   * @param {string | null | undefined} email
   */
  private handleFailedPasswordAttempt(email: string | null | undefined): void {
    if (this.loginCaptchaSrv.registerFailedAttempt(email)) this.isCaptchaRequired = true;
  }

  /**
   * Clears local captcha requirement state in sign in component
   */
  private clearCaptchaState(): void {
    this.isCaptchaRequired = false;
  }
}
